/**
 * Domain List View
 * Displays mail domains with verification status, DNS checks, and management actions
 */
import React, { useState, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  GlobeAltIcon,
  EllipsisVerticalIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  StarIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import {
  useDomainList,
  useDomain,
  useAddDomain,
  useVerifyDomain,
  useSetPrimaryDomain,
  useDeleteDomain,
  useRegenerateDkim,
} from '../../hooks/useDomains';
import type { Domain, DomainStatus, DomainDnsRecords, DnsVerificationResult } from '../../types/admin.types';

// ============================================================================
// TYPES
// ============================================================================

type SortField = 'domain' | 'status' | 'created_at';
type SortOrder = 'asc' | 'desc';

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const StatusBadge: React.FC<{ status: DomainStatus }> = ({ status }) => {
  const config = {
    verified: { bg: 'bg-green-100 dark:bg-green-900/20', text: 'text-green-800 dark:text-green-300', icon: CheckCircleIcon, label: 'Verified' },
    pending: { bg: 'bg-amber-100 dark:bg-amber-900/20', text: 'text-amber-800 dark:text-amber-300', icon: ClockIcon, label: 'Pending' },
    failed: { bg: 'bg-red-100 dark:bg-red-900/20', text: 'text-red-800 dark:text-red-300', icon: ExclamationTriangleIcon, label: 'Failed' },
  }[status];

  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </span>
  );
};

const DnsCheck: React.FC<{ label: string; passed: boolean }> = ({ label, passed }) => (
  <span className={`inline-flex items-center text-xs ${passed ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`} title={`${label}: ${passed ? 'Verified' : 'Not verified'}`}>
    {passed ? <CheckCircleIcon className="w-3.5 h-3.5" /> : <XCircleIcon className="w-3.5 h-3.5" />}
  </span>
);

const CopyButton: React.FC<{ text: string; label?: string }> = ({ text, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
      title={`Copy ${label || 'to clipboard'}`}
    >
      {copied ? (
        <CheckCircleIcon className="w-3.5 h-3.5 text-green-500" />
      ) : (
        <ClipboardDocumentIcon className="w-3.5 h-3.5" />
      )}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

// ============================================================================
// DNS RECORDS PANEL
// ============================================================================

const DnsRecordsPanel: React.FC<{ domainId: string; onClose: () => void }> = ({ domainId, onClose }) => {
  const { data, isLoading } = useDomain(domainId);

  if (isLoading) {
    return (
      <tr>
        <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">Loading DNS records...</td>
      </tr>
    );
  }

  if (!data) return null;
  const { domain, dnsRecords } = data;

  const records = [
    { type: 'TXT (Verification)', name: domain.domain, value: dnsRecords.verification_txt },
    { type: 'MX', name: domain.domain, value: `${dnsRecords.mx.priority} ${dnsRecords.mx.host}` },
    { type: 'TXT (SPF)', name: domain.domain, value: dnsRecords.spf },
    { type: 'TXT (DKIM)', name: `${dnsRecords.dkim_selector}._domainkey.${domain.domain}`, value: dnsRecords.dkim_public_key },
    { type: 'TXT (DMARC)', name: `_dmarc.${domain.domain}`, value: dnsRecords.dmarc },
  ];

  return (
    <tr>
      <td colSpan={7} className="px-0 py-0">
        <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              DNS Records for {domain.domain}
            </h4>
            <button onClick={onClose} className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
              Close
            </button>
          </div>
          <div className="space-y-2">
            {records.map((rec, i) => (
              <div key={i} className="flex items-start gap-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <span className="text-xs font-mono font-medium text-lex-accent w-24 flex-shrink-0 pt-0.5">{rec.type}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-500 dark:text-gray-400">{rec.name}</div>
                  <div className="text-xs font-mono text-gray-800 dark:text-gray-200 break-all mt-0.5">{rec.value}</div>
                </div>
                <CopyButton text={rec.value} label={rec.type} />
              </div>
            ))}
          </div>
        </div>
      </td>
    </tr>
  );
};

// ============================================================================
// ADD DOMAIN MODAL
// ============================================================================

const AddDomainModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}> = ({ isOpen, onClose, tenantId }) => {
  const [domainName, setDomainName] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const addDomain = useAddDomain();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!domainName.trim()) return;

    try {
      await addDomain.mutateAsync({ tenantId, data: { domain: domainName.trim(), is_primary: isPrimary } });
      setDomainName('');
      setIsPrimary(false);
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add Domain</h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Domain Name
              </label>
              <input
                type="text"
                value={domainName}
                onChange={(e) => setDomainName(e.target.value)}
                placeholder="example.com"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
                autoFocus
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Set as primary domain</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!domainName.trim() || addDomain.isPending}
              className="px-4 py-2 text-sm font-medium text-lex-accent-contrast bg-lex-accent rounded-lg hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addDomain.isPending ? 'Adding...' : 'Add Domain'}
            </button>
          </div>
          {addDomain.isError && (
            <p className="mt-3 text-sm text-red-600 dark:text-red-400">
              {(addDomain.error as any)?.response?.data?.error || 'Failed to add domain'}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// VERIFICATION RESULT MODAL
// ============================================================================

const VerificationResultModal: React.FC<{
  result: { verification_result: DnsVerificationResult; is_verified: boolean } | null;
  onClose: () => void;
}> = ({ result, onClose }) => {
  if (!result) return null;

  const { verification_result: vr, is_verified } = result;
  const checks = [
    { label: 'Domain TXT', passed: vr.domain_verified },
    { label: 'MX Record', passed: vr.mx_valid },
    { label: 'SPF Record', passed: vr.spf_valid },
    { label: 'DKIM Record', passed: vr.dkim_valid },
    { label: 'DMARC Record', passed: vr.dmarc_valid },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-sm mx-4 p-6">
        <div className="flex items-center gap-2 mb-4">
          {is_verified ? (
            <CheckCircleIcon className="w-6 h-6 text-green-500" />
          ) : (
            <ExclamationTriangleIcon className="w-6 h-6 text-amber-500" />
          )}
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {is_verified ? 'Domain Verified' : 'Verification Incomplete'}
          </h3>
        </div>
        <div className="space-y-2 mb-4">
          {checks.map((c) => (
            <div key={c.label} className="flex items-center justify-between text-sm">
              <span className="text-gray-700 dark:text-gray-300">{c.label}</span>
              {c.passed ? (
                <CheckCircleIcon className="w-5 h-5 text-green-500" />
              ) : (
                <XCircleIcon className="w-5 h-5 text-red-400" />
              )}
            </div>
          ))}
        </div>
        {vr.errors.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
            <p className="text-xs font-medium text-red-700 dark:text-red-300 mb-1">Errors:</p>
            {vr.errors.map((err, i) => (
              <p key={i} className="text-xs text-red-600 dark:text-red-400">{err}</p>
            ))}
          </div>
        )}
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          Close
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const DomainListView: React.FC = () => {
  // State
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DomainStatus | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<SortField>('domain');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [expandedDnsId, setExpandedDnsId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Domain | null>(null);
  const [verificationResult, setVerificationResult] = useState<{ verification_result: DnsVerificationResult; is_verified: boolean } | null>(null);

  // Queries & mutations
  const { data: domains, isLoading, isError, refetch } = useDomainList();
  const verifyDomain = useVerifyDomain();
  const setPrimaryDomain = useSetPrimaryDomain();
  const deleteDomain = useDeleteDomain();
  const regenerateDkim = useRegenerateDkim();

  // Get tenantId from first domain (they all share the same tenant)
  const tenantId = domains?.[0]?.tenant_id || '';

  // Filtered & sorted domains
  const filteredDomains = useMemo(() => {
    if (!domains) return [];

    let result = [...domains];

    // Search filter
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((d) => d.domain.toLowerCase().includes(q));
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter((d) => d.status === statusFilter);
    }

    // Sort
    result.sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'domain': cmp = a.domain.localeCompare(b.domain); break;
        case 'status': cmp = a.status.localeCompare(b.status); break;
        case 'created_at': cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime(); break;
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [domains, search, statusFilter, sortBy, sortOrder]);

  // Handlers
  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleVerify = async (domainId: string) => {
    try {
      const result = await verifyDomain.mutateAsync(domainId);
      setVerificationResult(result);
    } catch {
      // Mutation error handled
    }
  };

  const handleSetPrimary = async (domainId: string) => {
    await setPrimaryDomain.mutateAsync(domainId);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    await deleteDomain.mutateAsync(deleteConfirm.domain_id);
    setDeleteConfirm(null);
  };

  const handleRegenerateDkim = async (domainId: string) => {
    await regenerateDkim.mutateAsync(domainId);
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? (
      <ChevronUpIcon className="w-3 h-3 ml-1 inline" />
    ) : (
      <ChevronDownIcon className="w-3 h-3 ml-1 inline" />
    );
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Domains</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage mail domains, DNS verification, and DKIM signing
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 bg-lex-accent text-lex-accent-contrast text-sm font-medium rounded-lg hover:bg-lex-accent-hover transition-colors"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Domain
        </button>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search domains..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`inline-flex items-center px-3 py-2 border rounded-lg text-sm ${
            showFilters
              ? 'border-lex-accent bg-lex-accent-soft text-lex-accent'
              : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <FunnelIcon className="w-4 h-4 mr-1.5" />
          Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as DomainStatus | 'all')}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">All</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          {(statusFilter !== 'all' || search) && (
            <button
              onClick={() => { setStatusFilter('all'); setSearch(''); }}
              className="text-xs text-lex-accent hover:underline mt-4"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700">
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                  onClick={() => handleSort('domain')}
                >
                  Domain <SortIcon field="domain" />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                  onClick={() => handleSort('status')}
                >
                  Status <SortIcon field="status" />
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  DNS Checks
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Primary
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                  onClick={() => handleSort('created_at')}
                >
                  Created <SortIcon field="created_at" />
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin mb-2" />
                      <span className="text-sm text-gray-500">Loading domains...</span>
                    </div>
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <ExclamationTriangleIcon className="w-8 h-8 text-red-400 mb-2" />
                      <span className="text-sm text-red-500">Failed to load domains</span>
                      <button onClick={() => refetch()} className="mt-2 text-xs text-lex-accent hover:underline">Retry</button>
                    </div>
                  </td>
                </tr>
              ) : filteredDomains.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <GlobeAltIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">No domains found</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {search || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Add your first domain to get started'}
                    </p>
                    {!search && statusFilter === 'all' && (
                      <button
                        onClick={() => setShowAddModal(true)}
                        className="mt-3 inline-flex items-center px-3 py-1.5 bg-lex-accent text-lex-accent-contrast text-xs font-medium rounded-lg hover:bg-lex-accent-hover"
                      >
                        <PlusIcon className="w-3 h-3 mr-1" />
                        Add Domain
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                filteredDomains.flatMap((domain) => {
                  const rows: React.ReactNode[] = [];

                  rows.push(
                    <tr key={domain.domain_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      {/* Domain Name */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <GlobeAltIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{domain.domain}</span>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge status={domain.status} />
                      </td>

                      {/* DNS Checks */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">MX</span>
                          <DnsCheck label="MX" passed={domain.mx_verified} />
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">SPF</span>
                          <DnsCheck label="SPF" passed={domain.spf_verified} />
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">DKIM</span>
                          <DnsCheck label="DKIM" passed={domain.dkim_verified} />
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">DMARC</span>
                          <DnsCheck label="DMARC" passed={domain.dmarc_verified} />
                        </div>
                      </td>

                      {/* Primary */}
                      <td className="px-4 py-3 text-center">
                        {domain.is_primary ? (
                          <StarIconSolid className="w-4 h-4 text-amber-500 mx-auto" />
                        ) : (
                          <StarIcon className="w-4 h-4 text-gray-300 dark:text-gray-600 mx-auto" />
                        )}
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs font-medium ${
                          domain.domain_type === 'system'
                            ? 'text-lex-accent'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {domain.domain_type === 'system' ? 'System' : 'Custom'}
                        </span>
                      </td>

                      {/* Created */}
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(domain.created_at)}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger>
                            <EllipsisVerticalIcon className="w-5 h-5 text-gray-500" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setExpandedDnsId(expandedDnsId === domain.domain_id ? null : domain.domain_id)}>
                              <DocumentDuplicateIcon className="w-4 h-4 mr-2" />
                              {expandedDnsId === domain.domain_id ? 'Hide' : 'View'} DNS Records
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleVerify(domain.domain_id)}
                              disabled={verifyDomain.isPending}
                            >
                              <ShieldCheckIcon className="w-4 h-4 mr-2" />
                              {verifyDomain.isPending ? 'Verifying...' : 'Verify Now'}
                            </DropdownMenuItem>
                            {!domain.is_primary && domain.status === 'verified' && (
                              <DropdownMenuItem onClick={() => handleSetPrimary(domain.domain_id)}>
                                <StarIcon className="w-4 h-4 mr-2" />
                                Set as Primary
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleRegenerateDkim(domain.domain_id)}>
                              <ArrowPathIcon className="w-4 h-4 mr-2" />
                              Regenerate DKIM
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setDeleteConfirm(domain)}
                              disabled={domain.is_primary}
                              className="text-red-600 dark:text-red-400"
                            >
                              <TrashIcon className="w-4 h-4 mr-2" />
                              Delete Domain
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );

                  // Expanded DNS records row
                  if (expandedDnsId === domain.domain_id) {
                    rows.push(
                      <DnsRecordsPanel
                        key={`dns-${domain.domain_id}`}
                        domainId={domain.domain_id}
                        onClose={() => setExpandedDnsId(null)}
                      />
                    );
                  }

                  return rows;
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {domains && domains.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Showing {filteredDomains.length} of {domains.length} domain{domains.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      {/* Add Domain Modal */}
      <AddDomainModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        tenantId={tenantId}
      />

      {/* Verification Result Modal */}
      <VerificationResultModal
        result={verificationResult}
        onClose={() => setVerificationResult(null)}
      />

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete Domain</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete <strong>{deleteConfirm.domain}</strong>? This will remove all associated DNS configurations. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteDomain.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteDomain.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
